import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
	IconArrowDown,
	IconArrowUp,
	IconCheck,
	IconCreditCard,
	IconReceipt,
} from "@tabler/icons-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/convex-api";

type TransactionTypeFilter =
	| "all"
	| "deposit"
	| "escrow_lock"
	| "escrow_release"
	| "runner_payout"
	| "platform_fee"
	| "goods_refund";

type TransactionStatusFilter = "all" | "pending" | "completed" | "failed";

function formatNaira(kobo: number): string {
	return `₦${(kobo / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

export const Route = createFileRoute("/_authenticated/finances")({
	component: FinancesPage,
});

function FinancesPage() {
	const stats = useQuery(api.admin.getDashboardStats);
	const [typeFilter, setTypeFilter] = useState<TransactionTypeFilter>("all");
	const [statusFilter, setStatusFilter] =
		useState<TransactionStatusFilter>("all");

	const transactions = useQuery(api.admin.listTransactions, {
		type: typeFilter === "all" ? undefined : typeFilter,
		status: statusFilter === "all" ? undefined : statusFilter,
		limit: 100,
	});
	const payouts = useQuery(api.admin.listPayouts, { limit: 100 });

	const markPayoutCompleted = useMutation(api.admin.markPayoutCompleted);
	const [markingId, setMarkingId] = useState<string | null>(null);
	const [message, setMessage] = useState<string | null>(null);

	const handleMarkPaid = async (transactionId: string) => {
		setMarkingId(transactionId);
		setMessage(null);
		try {
			await markPayoutCompleted({
				transactionId: transactionId as never,
			});
			setMessage("Payout marked as completed.");
		} catch {
			setMessage("Failed to mark payout.");
		} finally {
			setMarkingId(null);
		}
	};

	const isLoadingStats = stats === undefined;
	const isLoadingTransactions = transactions === undefined;
	const isLoadingPayouts = payouts === undefined;

	return (
		<div className="container mx-auto px-4 py-10 lg:px-6">
			<div className="mb-8">
				<h1 className="font-bold text-3xl">Financial Overview</h1>
				<p className="mt-1 text-muted-foreground text-sm">
					Monitor platform finances, transactions, and payouts.
				</p>
			</div>

			{/* Stats Cards */}
			<div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{isLoadingStats ? (
					Array.from({ length: 3 }).map((_, i) => (
						<Skeleton className="h-24" key={`stat-skeleton-${String(i)}`} />
					))
				) : stats ? (
					<>
						<Card>
							<CardHeader className="flex flex-row items-center justify-between pb-2">
								<CardTitle className="font-medium text-sm">
									Total Deposits
								</CardTitle>
								<IconArrowDown className="h-4 w-4 text-green-500" />
							</CardHeader>
							<CardContent>
								<div className="font-bold text-2xl">
									{formatNaira(stats.finances.totalDeposits)}
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="flex flex-row items-center justify-between pb-2">
								<CardTitle className="font-medium text-sm">
									Platform Fees
								</CardTitle>
								<IconReceipt className="h-4 w-4 text-blue-500" />
							</CardHeader>
							<CardContent>
								<div className="font-bold text-2xl">
									{formatNaira(stats.finances.totalPlatformFees)}
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="flex flex-row items-center justify-between pb-2">
								<CardTitle className="font-medium text-sm">
									Pending Payouts
								</CardTitle>
								<IconArrowUp className="h-4 w-4 text-orange-500" />
							</CardHeader>
							<CardContent>
								<div className="font-bold text-2xl">
									{formatNaira(stats.finances.pendingPayouts)}
								</div>
							</CardContent>
						</Card>
					</>
				) : null}
			</div>

			{/* Tabs: Transactions / Payouts */}
			<Tabs defaultValue="transactions">
				<TabsList>
					<TabsTrigger value="transactions">
						<IconCreditCard className="mr-2 h-4 w-4" />
						Transactions
					</TabsTrigger>
					<TabsTrigger value="payouts">
						<IconArrowUp className="mr-2 h-4 w-4" />
						Payouts
					</TabsTrigger>
				</TabsList>

				{/* Transactions Tab */}
				<TabsContent value="transactions" className="mt-4">
					<div className="mb-4 flex gap-3">
						<Select
							onValueChange={(value) =>
								setTypeFilter(value as TransactionTypeFilter)
							}
							value={typeFilter}
						>
							<SelectTrigger className="w-[200px]">
								<SelectValue placeholder="Transaction type" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Types</SelectItem>
								<SelectItem value="deposit">Deposit</SelectItem>
								<SelectItem value="escrow_lock">Escrow Lock</SelectItem>
								<SelectItem value="escrow_release">Escrow Release</SelectItem>
								<SelectItem value="runner_payout">Runner Payout</SelectItem>
								<SelectItem value="platform_fee">Platform Fee</SelectItem>
								<SelectItem value="goods_refund">Goods Refund</SelectItem>
							</SelectContent>
						</Select>
						<Select
							onValueChange={(value) =>
								setStatusFilter(value as TransactionStatusFilter)
							}
							value={statusFilter}
						>
							<SelectTrigger className="w-[180px]">
								<SelectValue placeholder="Status" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Statuses</SelectItem>
								<SelectItem value="pending">Pending</SelectItem>
								<SelectItem value="completed">Completed</SelectItem>
								<SelectItem value="failed">Failed</SelectItem>
							</SelectContent>
						</Select>
					</div>

					{isLoadingTransactions ? (
						<div className="space-y-3">
							{Array.from({ length: 8 }).map((_, i) => (
								<Skeleton
									className="h-12 w-full"
									key={`tx-skeleton-${String(i)}`}
								/>
							))}
						</div>
					) : !transactions || transactions.length === 0 ? (
						<div className="flex items-center justify-center rounded-md border py-10">
							<p className="text-muted-foreground">No transactions found.</p>
						</div>
					) : (
						<div className="rounded-md border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Type</TableHead>
										<TableHead>Amount</TableHead>
										<TableHead>Status</TableHead>
										<TableHead>Description</TableHead>
										<TableHead>Created</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{transactions.map((tx: (typeof transactions)[number]) => (
										<TableRow key={tx._id}>
											<TableCell>
												<Badge variant="outline">{tx.type}</Badge>
											</TableCell>
											<TableCell className="font-medium">
												{formatNaira(tx.amount)}
											</TableCell>
											<TableCell>
												<Badge
													variant={
														tx.status === "completed"
															? "default"
															: tx.status === "failed"
																? "destructive"
																: "secondary"
													}
												>
													{tx.status}
												</Badge>
											</TableCell>
											<TableCell className="max-w-[250px] truncate text-muted-foreground text-sm">
												{tx.description}
											</TableCell>
											<TableCell>
												{new Date(tx.createdAt).toLocaleString()}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</TabsContent>

				{/* Payouts Tab */}
				<TabsContent value="payouts" className="mt-4">
					{message && (
						<div className="mb-4 rounded-md border bg-muted p-3 text-sm">
							{message}
						</div>
					)}

					{isLoadingPayouts ? (
						<div className="space-y-3">
							{Array.from({ length: 5 }).map((_, i) => (
								<Skeleton
									className="h-12 w-full"
									key={`payout-skeleton-${String(i)}`}
								/>
							))}
						</div>
					) : !payouts || payouts.length === 0 ? (
						<div className="flex items-center justify-center rounded-md border py-10">
							<p className="text-muted-foreground">No payouts found.</p>
						</div>
					) : (
						<div className="rounded-md border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Runner</TableHead>
										<TableHead>Amount</TableHead>
										<TableHead>Status</TableHead>
										<TableHead>Created</TableHead>
										<TableHead>Completed</TableHead>
										<TableHead>Actions</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{payouts.map((payout: (typeof payouts)[number]) => (
										<TableRow key={payout.id}>
											<TableCell>
												{payout.user ? (
													<div>
														<div className="font-medium">
															{payout.user.name}
														</div>
														<div className="text-muted-foreground text-xs">
															{payout.user.phoneNumber ?? payout.user.email}
														</div>
													</div>
												) : (
													<span className="text-muted-foreground">Unknown</span>
												)}
											</TableCell>
											<TableCell className="font-medium">
												{formatNaira(payout.amount)}
											</TableCell>
											<TableCell>
												<Badge
													variant={
														payout.payoutStatus === "paid"
															? "default"
															: payout.payoutStatus === "failed"
																? "destructive"
																: "secondary"
													}
												>
													{payout.payoutStatus ?? "pending"}
												</Badge>
											</TableCell>
											<TableCell>
												{new Date(payout.createdAt).toLocaleString()}
											</TableCell>
											<TableCell>
												{payout.payoutCompletedAt
													? new Date(payout.payoutCompletedAt).toLocaleString()
													: "—"}
											</TableCell>
											<TableCell>
												{payout.payoutStatus !== "paid" && (
													<Button
														disabled={markingId === payout.id}
														onClick={() => handleMarkPaid(payout.id)}
														size="sm"
													>
														<IconCheck className="mr-1 h-3 w-3" />
														{markingId === payout.id
															? "Marking..."
															: "Mark Paid"}
													</Button>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
